import { useQuery, useQueryClient, type QueryKey } from '@tanstack/vue-query';
import { computed, ref, toValue, watch, type MaybeRefOrGetter } from 'vue';

import { DEFAULT_OFFLINE_STALE_TIME } from '../constants.js';
import { createOfflineStorage, type OfflineStorage } from '../storage/offlineStorage.js';
import type { CacheMetadata, OfflineQueryOptions } from '../types.js';

import { useNetworkStatus } from './useNetworkStatus.js';

// Cache of storage instances per portal
const storageInstances = new Map<string, OfflineStorage>();

/**
 * Gets or creates an offline storage instance for a portal
 */
function getStorage(portalName: string): OfflineStorage {
  if (!storageInstances.has(portalName)) {
    storageInstances.set(portalName, createOfflineStorage({ portalName }));
  }
  return storageInstances.get(portalName)!;
}

/**
 * Converts a query key to a string for storage
 */
function queryKeyToString(queryKey: QueryKey): string {
  return JSON.stringify(queryKey);
}

/**
 * Options for useOfflineQuery
 */
export interface UseOfflineQueryOptions<TData = unknown, TError = Error> extends OfflineQueryOptions<TData, TError> {
  /** Portal name for namespace isolation */
  portalName: string;
}

/**
 * Composable for offline-aware data fetching with TanStack Query
 *
 * Extends TanStack Query's useQuery with:
 * - Automatic IndexedDB caching
 * - Stale-while-revalidate pattern for offline data
 * - Cache metadata (version, timestamp, etag)
 * - Offline status tracking
 *
 * @example
 * ```typescript
 * const { data, isFromCache, cacheTimestamp } = useOfflineQuery({
 *   portalName: 'client-portal',
 *   queryKey: ['invoices', partnerId],
 *   queryFn: () => api.fetchInvoices(partnerId),
 *   offlineKey: `invoices:${partnerId}`,
 * })
 * ```
 */
export function useOfflineQuery<TData = unknown, TError = Error>(
  queryKey: MaybeRefOrGetter<QueryKey>,
  queryFn: () => Promise<TData>,
  options: UseOfflineQueryOptions<TData, TError>,
) {
  const {
    portalName,
    offlineKey,
    persistOffline = true,
    offlineStaleTime = DEFAULT_OFFLINE_STALE_TIME,
    staleWhileRevalidate = true,
    ...queryOptions
  } = options;

  const storage = getStorage(portalName);
  const { isOnline } = useNetworkStatus();
  const queryClient = useQueryClient();

  // Internal state
  const isFromCache = ref(false);
  const cacheMetadata = ref<CacheMetadata | null>(null);
  const offlineError = ref<Error | null>(null);

  /**
   * Gets the storage key for this query
   */
  function getStorageKey(): string {
    return offlineKey ?? queryKeyToString(toValue(queryKey));
  }

  /**
   * Loads initial data from offline cache
   */
  async function loadFromCache(): Promise<TData | undefined> {
    if (!persistOffline) return undefined;

    try {
      const cached = await storage.get<TData>(getStorageKey());
      if (cached) {
        cacheMetadata.value = cached.metadata;
        isFromCache.value = true;
        return cached.data;
      }
    } catch (error) {
      console.warn('[useOfflineQuery] Failed to load from cache:', error);
    }
    return undefined;
  }

  /**
   * Saves data to offline cache
   */
  async function saveToCache(data: TData, metadata?: Partial<CacheMetadata>): Promise<void> {
    if (!persistOffline) return;

    try {
      await storage.set(getStorageKey(), data, metadata);
      cacheMetadata.value = {
        timestamp: Date.now(),
        version: metadata?.version ?? (cacheMetadata.value?.version ?? 0) + 1,
        etag: metadata?.etag,
        lastModified: metadata?.lastModified,
      };
    } catch (error) {
      console.warn('[useOfflineQuery] Failed to save to cache:', error);
    }
  }

  /**
   * Wrapped query function that handles offline caching
   */
  async function wrappedQueryFn(): Promise<TData> {
    // If offline, try to return cached data
    if (!isOnline.value) {
      const cached = await loadFromCache();
      if (cached !== undefined) {
        return cached;
      }
      throw new Error('No cached data available while offline');
    }

    // Online: fetch from server
    try {
      const data = await queryFn();

      // Extract metadata from response if available (convention)
      const metadata: Partial<CacheMetadata> = {};
      if (data && typeof data === 'object' && 'version' in data) {
        metadata.version = (data as Record<string, unknown>).version as number;
      }
      if (data && typeof data === 'object' && 'etag' in data) {
        metadata.etag = (data as Record<string, unknown>).etag as string;
      }

      // Save to cache
      await saveToCache(data, metadata);
      isFromCache.value = false;

      return data;
    } catch (error) {
      // On network error, try to return cached data if stale-while-revalidate is enabled
      if (staleWhileRevalidate) {
        const cached = await loadFromCache();
        if (cached !== undefined) {
          offlineError.value = error as Error;
          return cached;
        }
      }
      throw error;
    }
  }

  // Use TanStack Query with wrapped function
  const query = useQuery<TData, TError>({
    queryKey: toValue(queryKey),
    queryFn: wrappedQueryFn,
    // Use cached data as initial data if available
    initialData: undefined, // Will be set by placeholderData
    placeholderData: (previousData) => previousData,
    staleTime: isOnline.value ? queryOptions.staleTime : offlineStaleTime,
    ...queryOptions,
  });

  // Computed properties
  const cacheAge = computed(() => {
    if (!cacheMetadata.value) return null;
    return Date.now() - cacheMetadata.value.timestamp;
  });

  const cacheTimestamp = computed(() => {
    return cacheMetadata.value?.timestamp ?? null;
  });

  const cacheVersion = computed(() => {
    return cacheMetadata.value?.version ?? null;
  });

  const isStale = computed(() => {
    if (!cacheAge.value) return false;
    return cacheAge.value > offlineStaleTime;
  });

  const hasOfflineData = computed(() => {
    return cacheMetadata.value !== null;
  });

  /**
   * Invalidates the offline cache for this query
   */
  async function invalidateCache(): Promise<void> {
    await storage.delete(getStorageKey());
    cacheMetadata.value = null;
    isFromCache.value = false;
    // Also invalidate TanStack Query cache
    await queryClient.invalidateQueries({ queryKey: toValue(queryKey) });
  }

  /**
   * Forces a refresh from the server (if online)
   */
  async function forceRefresh(): Promise<void> {
    if (!isOnline.value) {
      throw new Error('Cannot refresh while offline');
    }
    await queryClient.refetchQueries({ queryKey: toValue(queryKey) });
  }

  /**
   * Prefetches and caches data for offline use
   */
  async function prefetchForOffline(): Promise<void> {
    if (!isOnline.value) {
      throw new Error('Cannot prefetch while offline');
    }
    await queryClient.prefetchQuery({
      queryKey: toValue(queryKey),
      queryFn: wrappedQueryFn,
    });
  }

  // Load from cache on mount (async)
  void loadFromCache().then((cached) => {
    if (cached !== undefined && !query.data.value) {
      // Set as placeholder data if no data yet
      queryClient.setQueryData(toValue(queryKey), cached);
    }
  });

  // Watch for online status changes to refetch
  watch(isOnline, (online) => {
    if (online && query.isStale) {
      void query.refetch();
    }
  });

  return {
    // TanStack Query result
    ...query,

    // Offline-specific state
    isFromCache: computed(() => isFromCache.value),
    cacheMetadata: computed(() => cacheMetadata.value),
    cacheAge,
    cacheTimestamp,
    cacheVersion,
    isStale,
    hasOfflineData,
    offlineError: computed(() => offlineError.value),

    // Offline-specific methods
    invalidateCache,
    forceRefresh,
    prefetchForOffline,
  };
}
